import { useEffect, useState } from "react";
import { Card, Cluster, ProgressBar, Stack } from "@salesapp/ui";
import { opsApi, type AiAllowance, type AiUsageArea, type AiUsageSummary } from "../lib/opsApi";
import { moneyLabel } from "../lib/planPricing";
import { useLocale } from "../shell/LocaleProvider";
import "../loxley/loxley.css";

const TOUCHPOINTS: Array<{ key: AiUsageArea["key"]; label: string }> = [
  { key: "leadgen", label: "LeadGen" },
  { key: "mailbox", label: "Mailbox" },
  { key: "loxley", label: "Loxley" },
];

function tokenCount(value: number): string {
  return Number(value || 0).toLocaleString("en-US");
}

function percent(value: number): string {
  return `${Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
}

/** "A$26.73 of A$49.00" once the service has been paid for, otherwise the share as a percentage. */
export function usedLabel(row: AiAllowance): string {
  if (row.used_cents == null || !row.currency) return `${percent(row.used_percent)} used`;
  return `${moneyLabel(row.used_cents, row.currency)} of ${moneyLabel(row.monthly_cents ?? 0, row.currency)} used`;
}

export function leftLabel(row: AiAllowance): string {
  if (row.remaining_cents == null || !row.currency) return `${percent(100 - row.used_percent)} left`;
  return `${moneyLabel(row.remaining_cents, row.currency)} left`;
}

function usageLine(row: AiAllowance): string | null {
  if (row.units) return `${tokenCount(row.units.used)} of ${tokenCount(row.units.limit)} ${row.units.name} this month.`;
  return row.detail ? `${row.detail}.` : null;
}

function tone(row: AiAllowance): "primary" | "warning" | "danger" {
  if (row.used_percent >= 100) return "danger";
  return row.used_percent >= 80 ? "warning" : "primary";
}

function areaRows(usage: AiUsageSummary | null): AiUsageArea[] {
  return TOUCHPOINTS.map((item) => {
    const found = usage?.areas?.find((area) => area.key === item.key);
    return found ?? { key: item.key, label: item.label, input_tokens: 0, output_tokens: 0, total_tokens: 0 };
  });
}

/** Each paid AI service's use this month, as money used and left, for the whole organization. */
export function AiConnectionPanel({ orgId }: { orgId: string }) {
  const { formatDate } = useLocale();
  const [loaded, setLoaded] = useState(false);
  const [usage, setUsage] = useState<AiUsageSummary | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);

  useEffect(() => {
    opsApi
      .getAiUsage(orgId)
      .then(setUsage)
      .catch((err) => setUsageError(err instanceof Error ? err.message : "AI usage could not be loaded."))
      .finally(() => setLoaded(true));
  }, [orgId]);

  if (!loaded) return <p>Loading AI settings…</p>;

  const allowances = usage?.allowances ?? [];
  const resets = usage?.resets_on ? formatDate(usage.resets_on) : null;

  return (
    <Stack gap={4}>
      <Card>
        <Stack gap={3}>
          <div>
            <h2 className="home-section-title">AI services this month</h2>
            <p className="home-subtitle">
              How much of each AI service you pay for has been used, for your whole organization: every user and
              every Loxley seat together, not each seat.{resets ? ` Resets on ${resets}.` : ""}
            </p>
          </div>
          {usageError && <p className="settings-error">{usageError}</p>}
          {allowances.length === 0 && !usageError ? (
            <p>No AI services are active. Saleslox can send you an offer under Billing.</p>
          ) : null}
          {allowances.map((row) => (
            <Stack key={row.addon} gap={1}>
              <Cluster justify="between" align="start">
                <div>
                  <strong>{row.label}</strong>
                  {usageLine(row) ? <p className="home-subtitle">{usageLine(row)}</p> : null}
                </div>
                <div className="loxley-num">
                  <div>{usedLabel(row)}</div>
                  <strong>{leftLabel(row)}</strong>
                </div>
              </Cluster>
              <ProgressBar value={row.used_percent} tone={tone(row)} />
              {row.used_percent >= 100 ? (
                <p className="settings-error">
                  {`${row.label} has used this month's allowance. Contact Saleslox to add more before ${resets ?? "next month"}.`}
                </p>
              ) : null}
            </Stack>
          ))}
        </Stack>
      </Card>
      {usage && !usage.using_platform ? (
        <Card>
          <Stack gap={3}>
            <div>
              <h2 className="home-section-title">Your AI connection</h2>
              <p className="home-subtitle">
                Your organization uses its own AI provider, so these tokens are billed to you by that provider.
              </p>
            </div>
            <table className="loxley-table">
              <thead>
                <tr>
                  <th>Touchpoint</th>
                  <th className="loxley-num">Input tokens</th>
                  <th className="loxley-num">Output tokens</th>
                  <th className="loxley-num">Total tokens</th>
                </tr>
              </thead>
              <tbody>
                {areaRows(usage).map((area) => (
                  <tr key={area.key}>
                    <td>{area.label}</td>
                    <td className="loxley-num">{tokenCount(area.input_tokens)}</td>
                    <td className="loxley-num">{tokenCount(area.output_tokens)}</td>
                    <td className="loxley-num">{tokenCount(area.total_tokens)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Stack>
        </Card>
      ) : null}
    </Stack>
  );
}
